import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { ApiResponse, ApiError, LoginRequest, RegisterRequest, PracticeKnowledge, AssessmentCoverage, PortfolioCoverageResponse, FeedbackStats, SkillFeedback, FoundingPartnerAccess, ComplianceScore } from '../types';
import { FOUNDING_PERIOD_ENDED, FOUNDING_PERIOD_ENDED_EVENT } from '../lib/foundingAccess';

/** Outcome statistics returned by AgencyOS `/api/agent-os/outcomes/stats` */
export interface AgencyOSOutcomeStats {
  period_days: number;
  total_outcomes: number;
  promotions: number;
  rollbacks: number;
  proposals: number;
  no_actions: number;
  promotion_rate: number;
  rollback_rate: number;
}

// CSRF token storage (not httpOnly so JS can read it)
let csrfToken: string | null = null;

// The csrf_token cookie is deliberately readable so the double-submit header
// can survive a full page reload — the in-memory copy dies with the old JS
// context, and without this fallback every mutation after a reload fails CSRF.
function readCsrfCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

// Endpoints where a 401 is a real answer to the caller rather than an expired
// session, so the refresh-and-redirect path must not swallow it:
//   /auth/login     — bad credentials, and MFA_REQUIRED / INVALID_MFA, which
//                     LoginPage reads to reveal the TOTP field. Refreshing here
//                     reloads the page before the form can show that field, so
//                     no MFA-enabled user can sign in.
//   /auth/register  — a rejected signup.
//   /auth/refresh   — the session really is gone; retrying recurses.
//   /auth/csrf, /auth/me — bootstrap calls made before a session exists.
const NO_SESSION_REFRESH_PATHS = [
  '/auth/csrf',
  '/auth/me',
  '/auth/login',
  '/auth/register',
  '/auth/refresh',
  // Pre-session WebAuthn ceremony calls: 4xx here is a real answer
  // (expired pending token, unregistered key), not an expired session.
  '/auth/mfa/webauthn/login/options',
  '/auth/mfa/webauthn/login/verify',
];

class ApiClient {
  private client: AxiosInstance;
  private csrfPromise: Promise<string> | null = null;

  // GLM-5.2 security fix: shared refresh state prevents concurrent 401s
  // from triggering multiple refresh calls (which would invalidate the session)
  private isRefreshing = false;
  private refreshPromise: Promise<void> | null = null;

  public getExportUrl(path: string): string {
    const base = import.meta.env.VITE_API_URL || '/api';
    return `${base.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
  }

  constructor() {
    this.client = axios.create({
      // @ts-ignore
      baseURL: import.meta.env.VITE_API_URL || '/api',
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true, // Send cookies with every request
    });

    // Request interceptor to add CSRF token (falling back to the readable
    // csrf_token cookie so sessions survive page reloads)
    this.client.interceptors.request.use(
      async (config) => {
        const token = csrfToken ?? readCsrfCookie();
        if (token) {
          config.headers['X-CSRF-Token'] = token;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor to handle token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // ADR-0029: the founding-partner period ended mid-session. Tell the
        // app shell to show the upgrade prompt; the caller still gets the error.
        if (error.response?.status === 402
            && error.response?.data?.error?.code === FOUNDING_PERIOD_ENDED
            && typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent(FOUNDING_PERIOD_ENDED_EVENT, {
            detail: error.response.data.error.details ?? null,
          }));
        }

        const requestPath = originalRequest?.url?.split('?')[0];
        if (error.response?.status === 401 && !originalRequest._retry
            && !NO_SESSION_REFRESH_PATHS.includes(requestPath)) {
          originalRequest._retry = true;

          try {
            await this.handleTokenRefresh();
            return this.client(originalRequest);
          } catch (refreshError) {
            // Refresh failed — redirect to login
            window.location.href = '/login';
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // GLM-5.2 security fix: single shared refresh promise prevents race
  // conditions where concurrent 401s would each trigger a refresh and
  // invalidate the session (backend rotates sessionVersion per refresh)
  private async handleTokenRefresh(): Promise<void> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshPromise = this.client.post('/auth/refresh')
        .then((response) => {
          if (response.data.success) {
            csrfToken = response.data.csrfToken || null;
          }
        })
        .finally(() => {
          this.isRefreshing = false;
          this.refreshPromise = null;
        });
    }
    return this.refreshPromise!;
  }

  private async fetchCsrfToken(): Promise<string> {
    if (csrfToken) return csrfToken;
    if (this.csrfPromise) return this.csrfPromise;

    this.csrfPromise = this.client.get('/auth/csrf').then((res) => {
      csrfToken = res.data.csrfToken || res.data.data?.csrfToken || null;
      return csrfToken || '';
    }).finally(() => {
      this.csrfPromise = null;
    });

    return this.csrfPromise;
  }

  // Generic request method
  async request<T = any>(config: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await this.client.request<ApiResponse<T>>(config)
      return response.data
    } catch (error: any) {
      if (error.response?.data) {
        throw error.response.data as ApiError
      }
      throw {
        error: {
          code: 'NETWORK_ERROR',
          message: 'Network error occurred',
        },
      } as ApiError
    }
  }

  // Auth endpoints
  async login(credentials: LoginRequest): Promise<ApiResponse<{ user: any }>> {
    // Fetch CSRF token BEFORE the request (backend requires it for auth mutations)
    await this.fetchCsrfToken();
    const result = await this.request({
      method: 'POST',
      url: '/auth/login',
      data: credentials,
    });
    // Login rotates the csrf_token cookie server-side; adopt the fresh token or
    // every subsequent mutation sends the stale one and 401s.
    if (result.csrfToken) {
      csrfToken = result.csrfToken;
    }
    return result;
  }

  async register(userData: RegisterRequest): Promise<ApiResponse<{ user: any }>> {
    // Fetch CSRF token BEFORE the request (backend requires it for auth mutations)
    await this.fetchCsrfToken();
    const result = await this.request({
      method: 'POST',
      url: '/auth/register',
      data: userData,
    });
    // Registration rotates the csrf_token cookie server-side; adopt the fresh token.
    if (result.csrfToken) {
      csrfToken = result.csrfToken;
    }
    return result;
  }

  async forgotPassword(data: { email: string }): Promise<ApiResponse<any>> {
    return this.request({
      method: 'POST',
      url: '/auth/forgot-password',
      data,
    });
  }

  async resetPassword(data: { token: string; newPassword: string }): Promise<ApiResponse<any>> {
    return this.request({
      method: 'POST',
      url: '/auth/reset-password',
      data,
    });
  }

  async getCurrentUser(): Promise<ApiResponse<{ user: any }>> {
    return this.request({
      method: 'GET',
      url: '/auth/me',
    });
  }

  async verifyEmail(token: string): Promise<ApiResponse<any>> {
    return this.request({
      method: 'GET',
      url: `/auth/verify/${encodeURIComponent(token)}`,
    });
  }

  async resendVerification(): Promise<ApiResponse<any>> {
    return this.request({
      method: 'POST',
      url: '/auth/resend-verification',
    });
  }

  // MFA / TOTP
  async getMfaStatus(): Promise<
    ApiResponse<{
      enabled: boolean
      method: 'none' | 'totp' | 'webauthn' | 'both'
      hasTotp: boolean
      hasWebauthn: boolean
      backupCodesRemaining: number
      credentials: { id: string; deviceName: string; transports: string | null; createdAt: string; lastUsedAt: string | null }[]
      phishingResistantRequired: boolean
      phishingResistantSatisfied: boolean
    }>
  > {
    return this.request({ method: 'GET', url: '/auth/mfa/status' });
  }

  async enrollMfa(): Promise<ApiResponse<{ otpauthUrl: string; qrDataUrl: string; secret: string }>> {
    return this.request({ method: 'POST', url: '/auth/mfa/enroll' });
  }

  async activateMfa(token: string): Promise<ApiResponse<{ backupCodes: string[] }>> {
    return this.request({ method: 'POST', url: '/auth/mfa/activate', data: { token } });
  }

  async disableMfa(password: string): Promise<ApiResponse<any>> {
    return this.request({ method: 'POST', url: '/auth/mfa/disable', data: { password } });
  }

  async regenerateBackupCodes(password: string): Promise<ApiResponse<{ backupCodes: string[] }>> {
    return this.request({ method: 'POST', url: '/auth/mfa/backup-codes', data: { password } });
  }

  // MFA / WebAuthn (security keys, YubiKey) — ADR-0026
  async webauthnRegisterOptions(): Promise<ApiResponse<{ options: any }>> {
    return this.request({ method: 'POST', url: '/auth/mfa/webauthn/register/options' });
  }

  async webauthnRegisterVerify(registration: any, deviceName?: string): Promise<ApiResponse<{ backupCodes?: string[] }>> {
    return this.request({
      method: 'POST',
      url: '/auth/mfa/webauthn/register/verify',
      data: { registration, ...(deviceName ? { deviceName } : {}) },
    });
  }

  async webauthnLoginOptions(mfaPendingToken: string): Promise<ApiResponse<{ options: any }>> {
    return this.request({
      method: 'POST',
      url: '/auth/mfa/webauthn/login/options',
      data: { mfaPendingToken },
    });
  }

  async webauthnLoginVerify(mfaPendingToken: string, assertion: any): Promise<ApiResponse<{ user: any }>> {
    return this.request({
      method: 'POST',
      url: '/auth/mfa/webauthn/login/verify',
      data: { mfaPendingToken, assertion },
    });
  }

  async webauthnRemoveCredential(credentialId: string, password: string): Promise<ApiResponse<{ mfaEnabled: boolean; mfaMethod: string }>> {
    return this.request({
      method: 'DELETE',
      url: `/auth/mfa/webauthn/credentials/${credentialId}`,
      data: { password },
    });
  }

  async logout(): Promise<ApiResponse> {
    const result = await this.request({
      method: 'POST',
      url: '/auth/logout',
    });
    csrfToken = null;
    return result;
  }

  // Organization endpoints
  async getOrganization(id: string): Promise<ApiResponse<{ organization: any }>> {
    return this.request({
      method: 'GET',
      url: `/organizations/${id}`,
    });
  }

  async updateOrganization(id: string, data: any): Promise<ApiResponse<{ organization: any }>> {
    return this.request({
      method: 'PUT',
      url: `/organizations/${id}`,
      data,
    });
  }

  // Client endpoints
  async getClients(): Promise<ApiResponse<{ clients: any[] }>> {
    return this.request({
      method: 'GET',
      url: '/clients',
    });
  }

  async getPortfolioSummary(): Promise<ApiResponse<{ clients: any[]; stats: any }>> {
    return this.request({
      method: 'GET',
      url: '/portfolio/summary',
    });
  }

  async getCostAvoidance(): Promise<ApiResponse<{ today: number; week: number; month: number; blockedPrompts: number; aiCostSaved: number }>> {
    return this.request({
      method: 'GET',
      url: '/ai/cost-avoidance',
    });
  }

  async getThreats(severity?: string, limit?: number): Promise<ApiResponse<{ threats: any[]; total: number; lastUpdated: string; source: string }>> {
    const params = new URLSearchParams();
    if (severity) params.set('severity', severity);
    if (limit) params.set('limit', limit.toString());
    return this.request({
      method: 'GET',
      url: `/threats?${params.toString()}`,
    });
  }

  async getAlertPreferences(): Promise<ApiResponse<any>> {
    return this.request({ method: 'GET', url: '/alerts/preferences' });
  }

  async getAbuseEvents(limit?: number, severity?: string): Promise<ApiResponse<any>> {
    const params = new URLSearchParams();
    if (limit) params.set('limit', limit.toString());
    if (severity) params.set('severity', severity);
    return this.request({
      method: 'GET',
      url: `/abuse-events?${params.toString()}`,
    });
  }

  async updateAlertPreferences(data: any): Promise<ApiResponse<void>> {
    return this.request({ method: 'PUT', url: '/alerts/preferences', data });
  }

  async deleteClient(id: string): Promise<ApiResponse<void>> {
    return this.request({
      method: 'DELETE',
      url: `/clients/${id}`,
    });
  }

  async createClient(data: any): Promise<ApiResponse<{ client: any }>> {
    return this.request({
      method: 'POST',
      url: '/clients',
      data,
    });
  }

  // Engagement endpoints
  async getEngagements(params?: { clientId?: string; status?: string }): Promise<ApiResponse<{ engagements: any[] }>> {
    return this.request({
      method: 'GET',
      url: '/engagements',
      params,
    });
  }

  async getEngagement(id: string): Promise<ApiResponse<{ engagement: any }>> {
    return this.request({
      method: 'GET',
      url: `/engagements/${id}`,
    });
  }

  async createEngagement(data: any): Promise<ApiResponse<{ engagement: any }>> {
    return this.request({
      method: 'POST',
      url: '/engagements',
      data,
    });
  }

  async updateEngagement(id: string, data: any): Promise<ApiResponse<{ engagement: any }>> {
    return this.request({
      method: 'PATCH',
      url: `/engagements/${id}`,
      data,
    });
  }

  async deleteEngagement(id: string): Promise<ApiResponse<void>> {
    return this.request({
      method: 'DELETE',
      url: `/engagements/${id}`,
    });
  }

  async getClient(id: string): Promise<ApiResponse<{ client: any }>> {
    return this.request({
      method: 'GET',
      url: `/clients/${id}`,
    });
  }

  // Assessment endpoints
  async getAssessments(params?: { page?: number; limit?: number; status?: string }): Promise<ApiResponse<{ assessments: any[]; pagination: any }>> {
    return this.request({
      method: 'GET',
      url: '/assessments',
      params,
    });
  }

  async createAssessment(data: any): Promise<ApiResponse<{ assessment: any }>> {
    return this.request({
      method: 'POST',
      url: '/assessments',
      data,
    });
  }

  async getAssessment(id: string): Promise<ApiResponse<{ assessment: any; agencyosOutcomes: AgencyOSOutcomeStats | null }>> {
    return this.request({
      method: 'GET',
      url: `/assessments/${id}`,
    });
  }

  async updateAssessment(id: string, data: any): Promise<ApiResponse<{ assessment: any }>> {
    return this.request({
      method: 'PUT',
      url: `/assessments/${id}`,
      data,
    });
  }

  async completeAssessment(id: string): Promise<ApiResponse<{ assessment: any }>> {
    return this.request({
      method: 'POST',
      url: `/assessments/${id}/complete`,
    });
  }

  async deleteAssessment(id: string): Promise<ApiResponse> {
    return this.request({
      method: 'DELETE',
      url: `/assessments/${id}`,
    });
  }

  // CMMC Practice endpoints
  async getDomains(): Promise<ApiResponse<{ domains: any[] }>> {
    return this.request({
      method: 'GET',
      url: '/cmmc/domains',
    });
  }

  async getDomain(id: string): Promise<ApiResponse<{ domain: any }>> {
    return this.request({
      method: 'GET',
      url: `/cmmc/domains/${id}`,
    });
  }

  async getPractices(params?: { page?: number; limit?: number; level?: number; domain?: string }): Promise<ApiResponse<{ practices: any[]; pagination: any }>> {
    return this.request({
      method: 'GET',
      url: '/cmmc/practices',
      params,
    });
  }

  async getPractice(id: string): Promise<ApiResponse<{ practice: any }>> {
    return this.request({
      method: 'GET',
      url: `/cmmc/practices/${id}`,
    });
  }

  // Practice Assessment endpoints
  async getPracticeAssessments(assessmentId: string): Promise<ApiResponse<{ practiceAssessments: any[] }>> {
    return this.request({
      method: 'GET',
      url: `/assessments/${assessmentId}/practices`,
    });
  }

  async assessPractice(assessmentId: string, practiceId: string, data: any): Promise<ApiResponse<{ practiceAssessment: any }>> {
    return this.request({
      method: 'POST',
      url: `/assessments/${assessmentId}/practices/${practiceId}/assess`,
      data,
    });
  }

  async updatePracticeAssessment(id: string, data: any): Promise<ApiResponse<{ practiceAssessment: any }>> {
    return this.request({
      method: 'PUT',
      url: `/practice-assessments/${id}`,
      data,
    });
  }

  async getGaps(assessmentId: string): Promise<ApiResponse<{ gaps: any[] }>> {
    return this.request({
      method: 'GET',
      url: `/assessments/${assessmentId}/gaps`,
    });
  }

  async getComplianceScore(assessmentId: string): Promise<ApiResponse<ComplianceScore>> {
    return this.request({
      method: 'GET',
      url: `/assessments/${assessmentId}/compliance-score`,
    });
  }

  // AI endpoints
  async getAIGuidance(data: { practiceId: string; existingEvidence?: string; currentStatus?: string }): Promise<ApiResponse<{ guidance: string; practiceId: string }>> {
    return this.request({
      method: 'POST',
      url: '/ai/practice-guidance',
      data,
    });
  }

  async reviewEvidence(data: { practiceId: string; evidence: string }): Promise<ApiResponse<{ review: any; practiceId: string }>> {
    return this.request({
      method: 'POST',
      url: '/ai/review-evidence',
      data,
    });
  }

  // Deterministic CMMC domain knowledge for a practice (no AI call).
  async getPracticeKnowledge(practiceId: string): Promise<ApiResponse<{ knowledge: PracticeKnowledge | null }>> {
    return this.request({
      method: 'GET',
      url: `/cmmc/practices/${practiceId}/knowledge`,
    });
  }

  // Threat-informed defensive coverage for an assessment (deterministic, no AI).
  async getAssessmentCoverage(assessmentId: string): Promise<ApiResponse<AssessmentCoverage>> {
    return this.request({
      method: 'GET',
      url: `/assessments/${assessmentId}/coverage`,
    });
  }

  // Cross-client portfolio threat coverage rollup (Consultant tier).
  async getPortfolioCoverage(): Promise<ApiResponse<PortfolioCoverageResponse>> {
    return this.request({
      method: 'GET',
      url: '/portfolio/coverage',
    });
  }

  // Upload endpoints
  async uploadEvidence(file: File): Promise<ApiResponse<{ downloadPath: string; key: string; name: string }>> {
    const formData = new FormData()
    formData.append('file', file)
    return this.request({
      method: 'POST',
      url: '/upload/evidence',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  }

  // Report endpoints
  async generateReport(assessmentId: string, data: any): Promise<ApiResponse<{ report: any }>> {
    return this.request({
      method: 'POST',
      url: `/assessments/${assessmentId}/reports/generate`,
      data,
    });
  }

  async getReport(id: string): Promise<ApiResponse<{ report: any }>> {
    return this.request({
      method: 'GET',
      url: `/reports/${id}`,
    });
  }

  async downloadReportPDF(id: string): Promise<Blob> {
    const response = await this.client.get(`/reports/${id}/download`, {
      responseType: 'blob'
    })
    return response.data
  }

  async downloadSprsReport(assessmentId: string): Promise<Blob> {
    const response = await this.client.get(
      `/contractor/assessments/${assessmentId}/sprs-report`,
      { responseType: 'blob' }
    )
    return response.data
  }

  async getAssessmentReports(assessmentId: string): Promise<ApiResponse<{ reports: any[] }>> {
    return this.request({
      method: 'GET',
      url: `/assessments/${assessmentId}/reports`,
    });
  }

  // Subscription endpoints
  async getSubscriptionTiers(): Promise<ApiResponse<{ tiers: any[] }>> {
    return this.request({
      method: 'GET',
      url: '/subscriptions/tiers',
    });
  }

  async getSubscriptionDetails(): Promise<ApiResponse<{ organization: any; tierDetails: any; roi: any; upgradeRecommendations: any[]; usage?: any; foundingPartner?: FoundingPartnerAccess | null }>> {
    return this.request({
      method: 'GET',
      url: '/subscriptions/current',
    });
  }

  async getSubscriptionUsage(): Promise<ApiResponse<{ usage: any; tierDetails: any }>> {
    return this.request({
      method: 'GET',
      url: '/subscriptions/usage',
    });
  }

  async getSubscriptionROI(months: number = 12, tier?: string): Promise<ApiResponse<{ roi: any; tierDetails: any }>> {
    const q = tier ? `&tier=${encodeURIComponent(tier)}` : '';
    return this.request({
      method: 'GET',
      url: `/subscriptions/roi?months=${months}${q}`,
    })
  }

  async checkoutSubscription(tier: string): Promise<ApiResponse<{ url: string }>> {
    return this.request({
      method: 'POST',
      url: '/billing/checkout',
      data: { tier },
    })
  }

  async upgradeSubscription(tier: string): Promise<ApiResponse<{ organization: any; tierDetails: any }>> {
    return this.request({
      method: 'POST',
      url: '/subscriptions/upgrade',
      data: { newTier: tier },
    })
  }

  // CSV Export
  async downloadGapsCSV(assessmentId: string): Promise<Blob> {
    const response = await this.client.get(`/assessments/${assessmentId}/gaps/export`, {
      responseType: 'blob'
    })
    return response.data
  }

  /**
   * SPRS Export — JSON or PDF
   * Downloads a SPRS submission package. Defaults to JSON; pass format='pdf' for a formatted PDF report.
   */
  async exportSprs(assessmentId: string, options?: { format?: 'json' | 'pdf'; cageCode?: string; uei?: string }): Promise<Blob> {
    const params = new URLSearchParams()
    if (options?.format) params.set('format', options.format)
    if (options?.cageCode) params.set('cage_code', options.cageCode)
    if (options?.uei) params.set('uei', options.uei)
    const qs = params.toString()
    const response = await this.client.get(`/assessments/${assessmentId}/sprs-export${qs ? `?${qs}` : ''}`, {
      responseType: 'blob'
    })
    return response.data
  }

  // POA&M (Plan of Action & Milestones)
  async listPoam(assessmentId: string): Promise<ApiResponse<{ items: any[]; total: number }>> {
    return this.request({
      method: 'GET',
      url: `/assessments/${assessmentId}/poam`,
    })
  }

  async createPoam(
    assessmentId: string,
    data: {
      practiceId: string
      title: string
      description?: string
      remediation?: string
      owner?: string
      targetDate?: string
      status?: 'open' | 'in_progress' | 'blocked' | 'closed'
      costEstimate?: number
    },
  ): Promise<ApiResponse<{ item: any }>> {
    return this.request({
      method: 'POST',
      url: `/assessments/${assessmentId}/poam`,
      data,
    })
  }

  async updatePoam(
    assessmentId: string,
    itemId: string,
    data: Record<string, any>,
  ): Promise<ApiResponse<{ item: any }>> {
    return this.request({
      method: 'PATCH',
      url: `/assessments/${assessmentId}/poam/${itemId}`,
      data,
    })
  }

  async deletePoam(assessmentId: string, itemId: string): Promise<ApiResponse<void>> {
    return this.request({
      method: 'DELETE',
      url: `/assessments/${assessmentId}/poam/${itemId}`,
    })
  }

  // Evidence manager
  async listEvidence(assessmentId: string): Promise<ApiResponse<{ evidence: any[]; count: number }>> {
    return this.request({
      method: 'GET',
      url: `/assessments/${assessmentId}/evidence`,
    })
  }

  // Automated evidence collection (Microsoft 365, etc.)
  async getEvidenceProviders(
    assessmentId: string,
  ): Promise<ApiResponse<{ providers: Array<{ id: string; name: string; configured: boolean }> }>> {
    return this.request({
      method: 'GET',
      url: `/assessments/${assessmentId}/evidence/providers`,
    })
  }

  async collectEvidence(
    assessmentId: string,
    provider: string,
  ): Promise<ApiResponse<{ mode: 'live' | 'mock'; isLive: boolean; collected: number; signals: any[] }>> {
    return this.request({
      method: 'POST',
      url: `/assessments/${assessmentId}/evidence/collect`,
      data: { provider },
    });
  }

  // Evidence auto-linking — suggest practices from filename/description
  async suggestEvidenceLinks(
    assessmentId: string,
    body: { filename?: string; description?: string },
  ): Promise<ApiResponse<{ practiceIds: string[]; matched: string[] }>> {
    return this.request({
      method: 'POST',
      url: `/assessments/${assessmentId}/evidence/suggest-links`,
      data: body,
    })
  }

  // C-08: Compliance trend dashboard
  async getComplianceTrend(
    assessmentId: string,
    months: number = 6,
  ): Promise<ApiResponse<{
    points: Array<{
      date: string;
      score: number;
      totalSignals: number;
      compliantSignals: number;
      partialSignals: number;
      noncompliantSignals: number;
      regressions: number;
      improvements: number;
    }>;
    posture: 'improving' | 'declining' | 'stable';
    totalRuns: number;
    totalRegressions: number;
    totalImprovements: number;
    liveRuns: number;
  }>> {
    return this.request({
      method: 'GET',
      url: `/assessments/${assessmentId}/monitoring/trend?months=${months}`,
    });
  }

  // C-08: Monitoring config page
  async getMonitoringConfig(
    assessmentId: string,
  ): Promise<ApiResponse<{ schedules: Array<{ id: string; provider: string; frequency: 'daily' | 'weekly'; enabled: boolean; lastRunAt: string | null; due?: boolean }> }>> {
    return this.request({
      method: 'GET',
      url: `/assessments/${assessmentId}/monitoring/schedule`,
    });
  }

  async updateMonitoringConfig(
    assessmentId: string,
    body: { provider: string; enabled: boolean; frequency: 'daily' | 'weekly' },
  ): Promise<ApiResponse<{ schedule: any }>> {
    return this.request({
      method: 'PUT',
      url: `/assessments/${assessmentId}/monitoring/schedule`,
      data: body,
    });
  }

  // Continuous monitoring / drift
  async runMonitoring(
    assessmentId: string,
    provider: string,
  ): Promise<ApiResponse<{ mode: string; baseline: boolean; signals: any[]; drift: any[]; regressions: number }>> {
    return this.request({
      method: 'POST',
      url: `/assessments/${assessmentId}/monitoring/run`,
      data: { provider },
    })
  }

  async getMonitoringHistory(
    assessmentId: string,
  ): Promise<ApiResponse<{ history: any[]; count: number }>> {
    return this.request({
      method: 'GET',
      url: `/assessments/${assessmentId}/monitoring`,
    })
  }

  // Scheduled monitoring
  async getMonitoringSchedule(
    assessmentId: string,
  ): Promise<ApiResponse<{ schedules: any[] }>> {
    return this.request({
      method: 'GET',
      url: `/assessments/${assessmentId}/monitoring/schedule`,
    })
  }

  async setMonitoringSchedule(
    assessmentId: string,
    body: { provider: string; enabled: boolean; frequency: 'daily' | 'weekly' },
  ): Promise<ApiResponse<{ schedule: any }>> {
    return this.request({
      method: 'PUT',
      url: `/assessments/${assessmentId}/monitoring/schedule`,
      data: body,
    })
  }

  async uploadEvidenceForAssessment(
    assessmentId: string,
    file: File,
    opts?: { linkedPractices?: string[]; description?: string }
  ): Promise<ApiResponse<{ evidence: any }>> {
    const formData = new FormData()
    formData.append('file', file)
    if (opts?.linkedPractices?.length) formData.append('linkedPractices', opts.linkedPractices.join(','))
    if (opts?.description) formData.append('description', opts.description)
    return this.request({
      method: 'POST',
      url: `/assessments/${assessmentId}/evidence`,
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  }

  async updateEvidenceLinks(
    assessmentId: string,
    evidenceId: string,
    data: { linkedPractices: string[]; description?: string }
  ): Promise<ApiResponse<{ evidence: any }>> {
    return this.request({
      method: 'PUT',
      url: `/assessments/${assessmentId}/evidence/${evidenceId}/links`,
      data,
    })
  }

  async linkEvidence(
    assessmentId: string,
    evidenceId: string,
    practiceId: string
  ): Promise<ApiResponse<{ evidence: any }>> {
    return this.request({
      method: 'POST',
      url: `/assessments/${assessmentId}/evidence/${evidenceId}/link`,
      data: { practiceId },
    })
  }

  async deleteEvidence(assessmentId: string, evidenceId: string): Promise<ApiResponse> {
    return this.request({
      method: 'DELETE',
      url: `/assessments/${assessmentId}/evidence/${evidenceId}`,
    })
  }

  async validateEvidence(
    assessmentId: string,
    evidenceId: string,
    data: { status: 'pending' | 'approved' | 'rejected'; note?: string }
  ): Promise<ApiResponse<{ evidence: any }>> {
    return this.request({
      method: 'PATCH',
      url: `/assessments/${assessmentId}/evidence/${evidenceId}/validate`,
      data,
    })
  }

  // Review queue — cross-assessment view of evidence awaiting review
  async listReviewQueue(
    status: 'pending' | 'approved' | 'rejected' | 'all' = 'pending',
  ): Promise<ApiResponse<{ items: any[]; count: number }>> {
    const q = status === 'all' ? '' : `?status=${status}`
    return this.request({
      method: 'GET',
      url: `/assessments/evidence/review-queue${q}`,
    })
  }

  // ── Outreach leads ──────────────────────────────────────────────────────

  async listOutreachLeads(params?: {
    status?: string
    source?: string
    assignedTo?: string
  }): Promise<ApiResponse<{ leads: any[] }>> {
    const q = params ? '?' + new URLSearchParams(Object.entries(params).filter(([, v]) => v) as [string, string][]).toString() : ''
    return this.request({ method: 'GET', url: `/outreach/${q}` })
  }

  async getOutreachStats(): Promise<ApiResponse<{ stats: Record<string, number> }>> {
    return this.request({ method: 'GET', url: '/outreach/stats' })
  }

  async createOutreachLead(data: {
    companyName: string
    contactName?: string
    contactEmail?: string
    phone?: string
    linkedinUrl?: string
    naicsCode?: string
    employeeCount?: number
    source?: string
    status?: string
    notes?: string
    assignedTo?: string
  }): Promise<ApiResponse<{ lead: any }>> {
    return this.request({ method: 'POST', url: '/outreach/', data })
  }

  async getOutreachLead(id: string): Promise<ApiResponse<{ lead: any }>> {
    return this.request({ method: 'GET', url: `/outreach/${id}` })
  }

  async updateOutreachLead(id: string, data: Record<string, unknown>): Promise<ApiResponse<{ lead: any }>> {
    return this.request({ method: 'PATCH', url: `/outreach/${id}`, data })
  }

  async deleteOutreachLead(id: string): Promise<ApiResponse> {
    return this.request({ method: 'DELETE', url: `/outreach/${id}` })
  }

  async listLeadActivities(leadId: string): Promise<ApiResponse<{ activities: any[] }>> {
    return this.request({ method: 'GET', url: `/outreach/${leadId}/activities` })
  }

  async logLeadActivity(
    leadId: string,
    data: { type?: string; direction?: string; subject?: string; summary?: string; nextFollowUp?: string },
  ): Promise<ApiResponse<{ activity: any }>> {
    return this.request({ method: 'POST', url: `/outreach/${leadId}/activities`, data })
  }

  // CSV import
  async importOutreachLeads(file: File): Promise<ApiResponse<{ created: number; skipped: number; total: number; errors: Array<{ row: number; error: string }> }>> {
    const formData = new FormData()
    formData.append('file', file)
    return this.request({ method: 'POST', url: '/outreach/leads/import', data: formData, headers: { 'Content-Type': 'multipart/form-data' } })
  }

  // SSP generator
  async getSsp(assessmentId: string, templateId?: string): Promise<ApiResponse<any>> {
    const q = templateId ? `?templateId=${encodeURIComponent(templateId)}` : ''
    return this.request({ method: 'GET', url: `/assessments/${assessmentId}/ssp${q}` })
  }

  // Tamper-evident audit events for the caller's org (latest 500, newest first)
  async getAuditLogs(): Promise<ApiResponse<{ events: any[]; total: number }>> {
    return this.request({ method: 'GET', url: '/audit-logs' })
  }

  async verifyAuditChain(): Promise<ApiResponse<{ valid: boolean }>> {
    return this.request({ method: 'GET', url: '/audit-logs/verify' })
  }

  // Latest news signal for the banner
  async getLatestNews(): Promise<ApiResponse<{ signal: any; totalAvailable: number }>> {
    return this.request({ method: 'GET', url: '/news/latest' })
  }

  async getReadiness(assessmentId: string): Promise<ApiResponse<any>> {
    return this.request({ method: 'GET', url: `/assessments/${assessmentId}/readiness` })
  }

  // System asset inventory (#57)
  async listAssets(clientId: string): Promise<ApiResponse<{ assets: any[]; count: number; cuiBearing: number }>> {
    return this.request({ method: 'GET', url: `/clients/${clientId}/assets` })
  }
  async createAsset(clientId: string, data: any): Promise<ApiResponse<{ asset: any }>> {
    return this.request({ method: 'POST', url: `/clients/${clientId}/assets`, data })
  }
  async updateAsset(clientId: string, id: string, data: any): Promise<ApiResponse<{ asset: any }>> {
    return this.request({ method: 'PUT', url: `/clients/${clientId}/assets/${id}`, data })
  }
  async deleteAsset(clientId: string, id: string): Promise<ApiResponse> {
    return this.request({ method: 'DELETE', url: `/clients/${clientId}/assets/${id}` })
  }
  async importAssets(
    clientId: string,
    assets: Record<string, string>[]
  ): Promise<ApiResponse<{ created: number; failed: { row: number; error: string }[] }>> {
    return this.request({ method: 'POST', url: `/clients/${clientId}/assets/import`, data: { assets } })
  }

  async downloadEvidencePackagePdf(assessmentId: string): Promise<Blob> {
    const response = await this.client.get(
      `/assessments/${assessmentId}/readiness/evidence-package.pdf`,
      { responseType: 'blob' },
    )
    return response.data
  }

  async downloadSspPdf(assessmentId: string): Promise<Blob> {
    const response = await this.client.get(`/assessments/${assessmentId}/ssp/pdf`, {
      responseType: 'blob',
    })
    return response.data
  }

  async downloadPoamPdf(assessmentId: string): Promise<Blob> {
    const response = await this.client.get(`/assessments/${assessmentId}/poam/pdf`, {
      responseType: 'blob',
    })
    return response.data
  }

  async downloadPolicyDocx(
    templateId: string,
    params: { clientId: string; effectiveDate?: string; version?: string },
  ): Promise<Blob> {
    const response = await this.client.post(`/policies/${templateId}/docx`, params, {
      responseType: 'blob',
    })
    return response.data
  }

  // Auditor portal links
  async getAuditorLinks(assessmentId: string): Promise<ApiResponse<any>> {
    return this.request({ method: 'GET', url: `/assessments/${assessmentId}/auditor-links` })
  }

  async createAuditorLink(
    assessmentId: string,
    params: { expiresInDays?: number },
  ): Promise<ApiResponse<any>> {
    return this.request({ method: 'POST', url: `/assessments/${assessmentId}/auditor-links`, data: params })
  }

  async revokeAuditorLink(assessmentId: string, linkId: string): Promise<ApiResponse<any>> {
    return this.request({ method: 'DELETE', url: `/assessments/${assessmentId}/auditor-links/${linkId}` });
  }

  // Email inquiries (Zoho webhook)
  async getEmailInquiries(): Promise<ApiResponse<any>> {
    return this.request({ method: 'GET', url: '/email-inquiries' });
  }

  async markEmailInquiryRead(id: string): Promise<ApiResponse<any>> {
    return this.request({ method: 'PATCH', url: `/email-inquiries/${id}/read` });
  }

  async archiveEmailInquiry(id: string): Promise<ApiResponse<any>> {
    return this.request({ method: 'PATCH', url: `/email-inquiries/${id}/archive` });
  }

  // Integration scanners (frontend-facing helpers)
  async runConfigScan(data: {
    content: string
    filename: string
    targetAsset: string
  }): Promise<ApiResponse<any>> {
    return this.request({
      method: 'POST',
      url: '/integrations/config-scan',
      data,
    })
  }

  async runFindingsScan(data: {
    findings: any[]
    targetAsset: string
    engagementId?: string
  }): Promise<ApiResponse<any>> {
    return this.request({
      method: 'POST',
      url: '/integrations/scan',
      data,
    })
  }

  async cweLookup(cweIds: string[]): Promise<ApiResponse<any>> {
    return this.request({
      method: 'POST',
      url: '/integrations/cwe-lookup',
      data: { cweIds },
    });
  }

  // ── Agency OS: Skill Feedback (H3) ─────────────────────────────────────────

  async getFeedbackStats(): Promise<ApiResponse<FeedbackStats>> {
    return this.request({ method: 'GET', url: '/feedback/stats' });
  }

  async listFeedback(params?: {
    status?: string;
    skillName?: string;
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<{ rows: SkillFeedback[]; pagination: { total: number; limit: number; offset: number } }>> {
    const q = params ? '?' + new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '') as [string, string][]
    ).toString() : '';
    return this.request({ method: 'GET', url: `/feedback${q}` });
  }

  async getFeedbackById(id: string): Promise<ApiResponse<SkillFeedback>> {
    return this.request({ method: 'GET', url: `/feedback/${id}` });
  }

  async updateFeedbackStatus(id: string, status: string): Promise<ApiResponse<SkillFeedback>> {
    return this.request({ method: 'PATCH', url: `/feedback/${id}/status`, data: { status } });
  }

  async captureUserCorrection(data: {
    skillName: string;
    originalOutput: string;
    userEditedOutput: string;
    context?: string;
  }): Promise<ApiResponse<SkillFeedback>> {
    return this.request({ method: 'POST', url: '/feedback/capture/user-correction', data });
  }

  async captureTestFailure(data: {
    skillName: string;
    testName: string;
    errorMessage: string;
    commitSha?: string;
  }): Promise<ApiResponse<SkillFeedback>> {
    return this.request({ method: 'POST', url: '/feedback/capture/test-failure', data });
  }

  async captureQaFinding(data: {
    skillName: string;
    finding: string;
    severity: string;
    commitSha?: string;
  }): Promise<ApiResponse<SkillFeedback>> {
    return this.request({ method: 'POST', url: '/feedback/capture/qa-finding', data });
  }

  async generateFeedbackDigest(): Promise<ApiResponse<{ digest: string; generatedAt: string }>> {
    return this.request({ method: 'POST', url: '/feedback/digest' });
  }

  // ─── Phase 2: Readiness Score + Lead Capture ──────────────────────────────

  async captureLead(data: {
    email: string;
    name?: string;
    companyName?: string;
    state?: string;
    naicsCode?: string;
    employeeCount?: number;
    score: number;
    answers?: { questionId: number; points: number }[];
    source?: string;
  }): Promise<ApiResponse<{
    lead: { id: string; contactEmail: string; readinessScore: number; tier: string; tierLabel: string; summary: string };
    matchedConsultants: any[];
    totalListed: number;
  }>> {
    return this.request({ method: 'POST', url: '/leads/capture', data });
  }

  async getConsultantDirectory(params?: {
    state?: string;
    specialty?: string;
    c3pao?: boolean;
  }): Promise<ApiResponse<{
    consultants: any[];
    total: number;
  }>> {
    return this.request({ method: 'GET', url: '/consultants/directory', params });
  }

  // ─── Cloud Integrations: Azure (C-07) ──────────────────────────────────

  async getAzureIntegration(): Promise<ApiResponse<any>> {
    return this.request({ method: 'GET', url: '/integrations/azure' });
  }

  async connectAzureIntegration(data: {
    tenantId: string;
    clientId: string;
    clientSecret: string;
    subscriptionId?: string;
  }): Promise<ApiResponse<any>> {
    return this.request({ method: 'POST', url: '/integrations/azure', data });
  }

  async syncAzureIntegration(): Promise<ApiResponse<any>> {
    return this.request({ method: 'POST', url: '/integrations/azure/sync' });
  }

  async getAzureSyncStatus(): Promise<ApiResponse<any>> {
    return this.request({ method: 'GET', url: '/integrations/azure/sync-status' });
  }

  async deleteAzureIntegration(): Promise<ApiResponse<any>> {
    return this.request({ method: 'DELETE', url: '/integrations/azure' });
  }

  // Recon tools command reference (/tools). These endpoints return bare arrays.
  private async getToolsReference<T>(url: string): Promise<T[]> {
    const data: unknown = await this.request({ method: 'GET', url });
    return Array.isArray(data) ? (data as T[]) : [];
  }

  async getReconTools<T>(): Promise<T[]> {
    return this.getToolsReference<T>('/tools');
  }

  async getReconToolCategories<T>(toolId: string): Promise<T[]> {
    return this.getToolsReference<T>(`/tools/${encodeURIComponent(toolId)}/categories`);
  }

  async getReconToolCommands<T>(toolId: string): Promise<T[]> {
    return this.getToolsReference<T>(`/tools/${encodeURIComponent(toolId)}/commands`);
  }

  async searchReconCommands<T>(query: string): Promise<T[]> {
    return this.getToolsReference<T>(`/tools/search?q=${encodeURIComponent(query)}`);
  }
}

export const apiClient = new ApiClient()
